import type { Restaurant } from 'hooks/use-unit';
import { useQuery, useInfiniteQuery } from 'react-query';
import { api } from 'services/api';
import ICustomAxiosRequestConfig from 'services/custom/ICustomAxiosRequestConfig';
import type { Pagination } from 'types';

const FIFTEEN_MINUTES_STALE = 1000 * 60 * 15;

export type UseUnitsParams = {
  /**
   * Query by name, address, about and others text fields
   */
  query?: string;
  /**
   * Category identificator
   */
  cuisine_id?: string;
  service?: 'line' | 'reservation' | string;
  price_range?: string;
  date?: string;
  hour?: string;
  people?: string | number;
  /**
   * User's latitude
   */
  lat?: number | string;
  /**
   * User's longitude
   */
  lng?: number | string;
  /**
   * Distance in kilometers
   */
  distance?: number | string;
  pagination?: number;
  per_page?: number;
  page?: number;
  /**
   * For filtering with OnRiseNow restaurants
   */
  line_highlights?: number;
  /**
   * For filtering with NewInGetIn restaurants
   */
  recent?: number | string;
  /**
   * Filter by recommended restaurants
   */
  recommended?: number;

  /**
   * Filter by location (state, city, neighborhood)
   */
  state?: string;
  city?: string;
  neighborhood?: string;
} | null;

type UseUnitsOptions = {
  initialData?: {
    data: Restaurant[];
  };
  keepPreviousData?: boolean;
};

export type GetUnitsProps = {
  data: Restaurant[];
  pagination?: Pagination;
};

export const getUnits = async (
  params?: UseUnitsParams,
  serverSide?: { serverSide: boolean }
): Promise<GetUnitsProps> => {
  const { data } = await api.get('/restaurant/v1/units', {
    params,
    serverSide,
  } as ICustomAxiosRequestConfig);

  return {
    data: data.data,
    pagination: data.pagination,
  };
};

export const useUnits = (
  params?: UseUnitsParams,
  options?: UseUnitsOptions
) => {
  return useQuery('units', () => getUnits(params), {
    staleTime: FIFTEEN_MINUTES_STALE,
    ...options,
  });
};

export const useUnitsInfinite = (
  filters: UseUnitsParams,
  initialData?: GetUnitsProps
) =>
  useInfiniteQuery(
    'units-infinite-scroll',
    ({ pageParam = 1 }) => {
      return getUnits({ ...filters, page: pageParam });
    },
    {
      initialData: initialData
        ? {
            pages: [initialData],
            pageParams: [1],
          }
        : undefined,
      getNextPageParam: (lastPage) =>
        lastPage?.pagination?.next_page || undefined,
    }
  );
